/**
 * MeetingSettingsTab: 회의 설정 수정 화면
 * 마크업: src/markup/settingdialogmodify.html
 * 마스터만 접근 가능. InfoTab "회의설정 변경하기" 버튼으로 진입.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { localActions } from '@/store/slices/localSlice';
import { toastActions } from '@/store/slices/toastSlice';
import apiManager from '@/api';
import { getConferenceManager } from '@/conference/conferenceManagerRef';
import { CONFERENCE_COMMANDS } from '@/conference/commands';
import LUXDialog from '@/components/Dialog';
import type { LinkedMeeting } from '@/types/components';
import LinkedMeetingSearch from './LinkedMeetingSearch';
import { getMyCredit } from '@/api/translateAPI';
import { translateText } from '@/i18n';

interface MeetingSettingsTabProps {
  onBack: () => void;
}

const MeetingSettingsTab: React.FC<MeetingSettingsTabProps> = ({ onBack }) => {
  const dispatch = useAppDispatch();
  const local = useAppSelector((s) => s.local);
  const transcriptionRequested = useAppSelector(
    (s) => s.caption.isEnabled || s.translation.isCaptionEnabled || s.translation.isTranslateEnabled
  );
  const translationLanguage = useAppSelector((s) => s.translation.language);

  // ── 로딩 상태 ────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);

  // ── 폼 상태 ──────────────────────────────────────────────────────
  const [roomName, setRoomName] = useState(local.roomName || '');
  const [isPublic, setIsPublic] = useState(local.isPublic);
  const [securityType, setSecurityType] = useState<'PASSWORD' | 'MASTER_APPROVAL'>(
    local.securityType === 'MASTER_APPROVAL' ? 'MASTER_APPROVAL' : 'PASSWORD'
  );
  const [joinCode, setJoinCode] = useState(local.joinCode || '');
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [creditMode, setCreditMode] = useState<'company' | 'each'>('company');
  const [description, setDescription] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── 연결된 회의 ──────────────────────────────────────────────────
  const [linkedMeeting, setLinkedMeeting] = useState<LinkedMeeting | null>(null);
  const [linkedShareMode, setLinkedShareMode] = useState<'all' | 'previous'>('all');

  const keywordInputRef = useRef<HTMLInputElement>(null);

  // ── 마운트 시 편집정보 조회 API → 폼 초기값 세팅 ──────────────────
  useEffect(() => {
    let cancelled = false;

    const fetchDetail = async () => {
      try {
        const data = await apiManager.getRoomEditInfo(local.roomId);
        if (cancelled) return;

        setRoomName(data.name);
        setIsPublic(data.isPublic);

        if (data.securityType === 'MASTER_APPROVAL') {
          setSecurityType('MASTER_APPROVAL');
        } else {
          setSecurityType('PASSWORD');
        }
        setJoinCode(data.joincode ?? '');

        if (data.aiTranslationType) {
          setTranslationEnabled(true);
          setCreditMode(data.aiTranslationType === 'USE_PARTICIPANT_CREDIT' ? 'each' : 'company');
        }

        if (data.linkedRoomInfo) {
          const savedAccessType = data.linkedRoomInfo.accessType === 'PREVIOUS_ONLY' ? 'PREVIOUS_ONLY' : 'ALL';
          setLinkedMeeting({
            roomId: data.linkedRoomInfo.roomId,
            name: data.linkedRoomInfo.name ?? '',
            startDateTime: data.linkedRoomInfo.startDateTime,
            endDateTime: data.linkedRoomInfo.endDateTime,
            accessType: savedAccessType,
          });
          setLinkedShareMode(savedAccessType === 'PREVIOUS_ONLY' ? 'previous' : 'all');
        }

        // PROGRESS 방에서만 값 존재. RESERVED 방은 null/[] 로 응답됨.
        setDescription(data.topic ?? '');
        setKeywords(Array.isArray(data.keywordList) ? data.keywordList : []);
      } catch (err) {
        console.error('[MeetingSettings] 상세 조회 실패:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchDetail();
    return () => { cancelled = true; };
  }, [local.roomId]);

  // ── 연결된 회의 선택 핸들러 ────────────────────────────────────────
  const handleLinkedMeetingChange = useCallback((meeting: LinkedMeeting | null) => {
    setLinkedMeeting(meeting);
    if (!meeting) {
      setLinkedShareMode('all');
    }
  }, []);

  // ── 키워드 관리 ──────────────────────────────────────────────────
  const handleKeywordAdd = useCallback(() => {
    const trimmed = keywordInput.trim();
    if (!trimmed || keywords.includes(trimmed)) return;
    setKeywords((prev) => [...prev, trimmed]);
    setKeywordInput('');
  }, [keywordInput, keywords]);

  const handleKeywordRemove = useCallback((index: number) => {
    setKeywords((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleKeywordKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // 한글 IME 조합 중 Enter는 조합 확정으로만 처리하고 추가 입력은 무시
      if (e.key !== 'Enter') return;
      if (e.nativeEvent.isComposing || e.keyCode === 229) return;
      e.preventDefault();
      handleKeywordAdd();
    },
    [handleKeywordAdd]
  );

  // ── 회의 설정 변경 제출 ──────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (roomName.trim().length < 2) {
      LUXDialog.alert('최소 2자 이상 입력해주세요', {
        type: 'warning',
        useRenewalStyle: true,
      });
      return;
    }
    if (roomName.length > 30) {
      LUXDialog.alert('최대 30자 이하로 입력해주세요', {
        type: 'warning',
        useRenewalStyle: true,
      });
      return;
    }
    if (!isPublic && securityType === 'PASSWORD') {
      if (!joinCode.trim()) {
        LUXDialog.alert('암호를 입력해주세요.', {
          type: 'warning',
          useRenewalStyle: true,
        });
        return;
      }
      const reg = /^[0-9a-zA-Z]*$/;
      if (!reg.test(joinCode)) {
        LUXDialog.alert(translateText('암호는 영문, 숫자 [0-9, a-z, A-Z] 만 허용합니다.'), {
          type: 'warning',
          useRenewalStyle: true,
        });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const trimmedName = roomName.trim();
      const trimmedJoinCode = joinCode.trim();
      const trimmedTopic = description.trim();
      const finalSecurityType: 'PASSWORD' | 'MASTER_APPROVAL' | null = isPublic ? null : securityType;
      const finalJoinCode = !isPublic && securityType === 'PASSWORD' ? trimmedJoinCode : null;
      const aiTranslationType = translationEnabled
        ? (creditMode === 'each' ? 'USE_PARTICIPANT_CREDIT' : 'USE_CREATOR_CREDIT')
        : null;
      const finalLinkedRoomInfo = linkedMeeting
        ? {
            roomId: linkedMeeting.roomId,
            name: linkedMeeting.name ?? null,
            accessType: (linkedShareMode === 'previous' ? 'PREVIOUS_ONLY' : 'ALL') as 'ALL' | 'PREVIOUS_ONLY',
            startDateTime: linkedMeeting.startDateTime ?? null,
            endDateTime: linkedMeeting.endDateTime ?? null,
          }
        : null;

      // 진행 중(PROGRESS) 회의 — 예약 관련/참석자/첨부 필드는 서버에서 무시됨.
      await apiManager.updateRoom(local.roomId, {
        name: trimmedName,
        isPublic,
        securityType: finalSecurityType,
        joincode: finalJoinCode,
        aiTranslationType,
        linkedRoomInfo: finalLinkedRoomInfo,
        isReservation: false,
        topic: trimmedTopic || null,
        keywordList: keywords,
      });

      // Redux 로컬 상태 동기화 (InfoTab 즉시 반영) — 부분 병합으로 다른 필드 보존
      dispatch(localActions.mergeRoomInfo({
        roomName: trimmedName,
        isPublic,
        securityType: isPublic ? '' : securityType,
        joinCode: !isPublic && securityType === 'PASSWORD' ? trimmedJoinCode : '',
        aiTranslationType,
        linkedMeeting: linkedMeeting
          ? {
              roomId: linkedMeeting.roomId,
              name: linkedMeeting.name,
              startDateTime: linkedMeeting.startDateTime,
              endDateTime: linkedMeeting.endDateTime,
              accessType: linkedShareMode === 'previous' ? 'PREVIOUS_ONLY' : 'ALL',
            }
          : null,
      }));

      // AI 번역 메타데이터와 현재 사용자의 Jigasi 결과 수신 속성을 즉시 반영한다.
      try {
        getConferenceManager()?.getConnector()?.syncTranscriptionMode(transcriptionRequested, translationLanguage);
      } catch (syncErr) {
        console.warn('[MeetingSettings] Jigasi transcription 동기화 실패:', syncErr);
      }

      // 다른 참여자들에게 회의방 정보 변경 브로드캐스트 → 각 클라이언트가 재조회
      // sendCommandOnce + timestamp value: presence 잔존/중복 무시 방지
      try {
        const manager = getConferenceManager();
        const myUserId = manager?.getRoom()?.myUserId() ?? '';
        manager?.sendCommandOnce(CONFERENCE_COMMANDS.ROOM_INFO_UPDATED, {
          value: String(Date.now()),
          attributes: {
            senderId: myUserId,
            roomId: local.roomId,
            accessType: linkedShareMode === 'previous' ? 'PREVIOUS_ONLY' : (linkedMeeting ? 'ALL' : ''),
          },
        });
      } catch (broadcastErr) {
        console.warn('[MeetingSettings] ROOM_INFO_UPDATED 브로드캐스트 실패:', broadcastErr);
      }

      dispatch(toastActions.showSnackbar({
        type: 'success',
        message: '회의 설정이 변경되었습니다.',
      }));

      onBack();
    } catch (err: any) {
      console.error('[MeetingSettings] 변경 실패:', err);

      // 서버 측 유효성 검사 에러 처리 (400 Bad Request)
      const errorData = err.response?.data?.resultData?.errorData;
      if (Array.isArray(errorData)) {
        const nameError = errorData.find(
          (item: any) => item.param === 'name' && item.msg === 'name은 2자 이상 30자 이하이어야 합니다.'
        );
        if (nameError) {
          const message = roomName.trim().length < 2 ? '최소 2자 이상 입력해주세요' : '최대 30자 이하로 입력해주세요';
          LUXDialog.alert(message, {
            type: 'warning',
            useRenewalStyle: true,
          });
          return;
        }
      }

      LUXDialog.alert('회의 설정 변경에 실패했습니다.', {
        type: 'warning',
        useRenewalStyle: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    roomName, isPublic, securityType, joinCode, linkedMeeting,
    linkedShareMode, translationEnabled, creditMode, description,
    keywords, local.roomId, transcriptionRequested, translationLanguage, dispatch, onBack,
  ]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      {isLoading ? (
        <dl className="rtcDialogSetupNew__info" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <div className="rtcDialogSetupNew__meetingEmptySet">
            <p className="emptyText">회의 설정 정보를 불러오는 중입니다...</p>
          </div>
        </dl>
      ) : (
      <dl className="rtcDialogSetupNew__info">
        {/* ── 회의명 ──────────────────────────────────────────── */}
        <div>
          <dt className="textTop lh32">회의명</dt>
          <dd>
            <div className="common_textInputBoxWrap">
              <div className="common_textInputBox type4 notosans_font">
                <input
                  type="text"
                  className="common_textInput"
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                />
                {roomName && (
                  <button
                    type="button"
                    className="common_textInputBox__delbtn"
                    onClick={() => setRoomName('')}
                  >
                    <span className="blind">삭제</span>
                  </button>
                )}
              </div>
            </div>
          </dd>
        </div>

        {/* ── 연결된 회의 ─────────────────────────────────────── */}
        <div>
          <dt className="textTop lh32">연결된 회의</dt>
          <dd>
            <LinkedMeetingSearch
              value={linkedMeeting}
              onChange={handleLinkedMeetingChange}
            />
            {/* 연결된 회의 선택 시 공유 옵션 */}
            {linkedMeeting && (
              <div className="rtcDialogSetupNew__infoSetgroup1" style={{ marginTop: 12 }}>
                <div className="common_radio_input type_blue2 lhFix">
                  <label>
                    <input
                      className="radio_input"
                      type="radio"
                      name="linked_share_mode"
                      checked={linkedShareMode === 'all'}
                      onChange={() => setLinkedShareMode('all')}
                    />
                    <span className="icon" />
                    <span className="label_text">모든 참석자에게 이전 회의 내용 공유</span>
                  </label>
                </div>
                <div className="common_radio_input type_blue2 lhFix">
                  <label>
                    <input
                      className="radio_input"
                      type="radio"
                      name="linked_share_mode"
                      checked={linkedShareMode === 'previous'}
                      onChange={() => setLinkedShareMode('previous')}
                    />
                    <span className="icon" />
                    <span className="label_text">지난 회의 참석자에게만 이전 회의 내용 공유</span>
                  </label>
                </div>
              </div>
            )}
          </dd>
        </div>

        {/* ── 공개여부 ────────────────────────────────────────── */}
        <div>
          <dt className="textTop">공개여부</dt>
          <dd>
            <div className="rtcDialogSetupNew__infoSetgroup2">
              <p className="infoText">
                {isPublic
                  ? '초대받지 않은 인원도 회의 링크나 회의 ID를 통해 회의에 참가할 수 있습니다.'
                  : '회의 초대 링크 또는 암호를 통해 회의에 참가할 수 있습니다.'}
              </p>
              <div className="fixItem">
                <div className="common_onoff_switch small2 type_blue2">
                  <label>
                    <input
                      className="onoff_input"
                      type="checkbox"
                      checked={isPublic}
                      onChange={() => setIsPublic((prev) => !prev)}
                    />
                    <span className="switch_img" />
                    <span className="label_text on" />
                    <span className="label_text off" />
                  </label>
                </div>
              </div>
            </div>
            {/* 비공개일 때 보안 옵션 */}
            {!isPublic && (
              <div className="rtcDialogSetupNew__infoSetgroup4">
                <div className="infoSetgroup4__item">
                  <div className="common_radio_input type_blue2 lhFix">
                    <label>
                      <input
                        className="radio_input"
                        type="radio"
                        name="security_type"
                        checked={securityType === 'PASSWORD'}
                        onChange={() => setSecurityType('PASSWORD')}
                      />
                      <span className="icon" />
                      <span className="label_text">
                        암호
                        <span className="infoSetgroup4__subText">
                          암호가 있는 사용자만 회의에 참가할 수 있습니다.
                        </span>
                      </span>
                    </label>
                  </div>
                  {securityType === 'PASSWORD' && (
                    <div className="infoSetgroup4__inputBox">
                      <input
                        type="text"
                        className="infoSetgroup4__input"
                        value={joinCode}
                        onChange={(e) => {
                          const val = e.target.value;
                          const filteredVal = val.replace(/[^0-9a-zA-Z]/g, '');
                          if (val !== filteredVal) {
                            dispatch(
                              toastActions.showSnackbar({
                                type: 'error',
                                message: translateText('암호는 영문, 숫자 [0-9, a-z, A-Z] 만 허용합니다.'),
                              })
                            );
                          }
                          setJoinCode(filteredVal);
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="infoSetgroup4__item">
                  <div className="common_radio_input type_blue2 lhFix">
                    <label>
                      <input
                        className="radio_input"
                        type="radio"
                        name="security_type"
                        checked={securityType === 'MASTER_APPROVAL'}
                        onChange={() => setSecurityType('MASTER_APPROVAL')}
                      />
                      <span className="icon" />
                      <span className="label_text">
                        마스터 승인
                        <span className="infoSetgroup4__subText">
                          마스터가 참석 요청을 승인한 사용자만 회의에 참가할 수 있습니다.
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </dd>
        </div>

        {/* ── 번역회의 ────────────────────────────────────────── */}
        <div>
          <dt className="textTop">번역회의</dt>
          <dd>
            <div className="rtcDialogSetupNew__infoSetgroup2">
              <p className="infoText">
                크레딧을 사용하여 실시간 번역을 제공합니다. 크레딧이 부족할 경우 기능이 제한될 수 있습니다.
              </p>
              <div className="fixItem">
                <div className="common_onoff_switch small2 type_blue2">
                  <label>
                    <input
                      className="onoff_input"
                      type="checkbox"
                      checked={translationEnabled}
                      onChange={async (e) => {
                        const isChecked = e.target.checked;
                        if (isChecked) {
                          try {
                            const creditRes = await getMyCredit();
                            if (creditRes && creditRes.resultData && creditRes.resultData.total_credit > 0) {
                              setTranslationEnabled(true);
                            } else {
                              dispatch(toastActions.showSnackbar({ type: 'error', message: '사용 가능한 ONE AI 크레딧이 없습니다.' }));
                              setTranslationEnabled(false);
                            }
                          } catch (err) {
                            dispatch(toastActions.showSnackbar({ type: 'error', message: '크레딧 조회에 실패했습니다.' }));
                            setTranslationEnabled(false);
                          }
                        } else {
                          setTranslationEnabled(false);
                        }
                      }}
                    />
                    <span className="switch_img" />
                    <span className="label_text on" />
                    <span className="label_text off" />
                  </label>
                </div>
              </div>
            </div>
            {translationEnabled && (
              <div className="rtcDialogSetupNew__infoSetgroup4">
                <div className="infoSetgroup4__item">
                  <div className="common_radio_input type_blue2 lhFix">
                    <label>
                      <input
                        className="radio_input"
                        type="radio"
                        name="credit_mode"
                        checked={creditMode === 'company'}
                        onChange={() => setCreditMode('company')}
                      />
                      <span className="icon" />
                      <span className="label_text">
                        우리회사 크레딧 사용
                        <span className="infoSetgroup4__subText">
                          외부 참석자들도 회사 크레딧으로 번역 사용을 허용합니다.
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
                <div className="infoSetgroup4__item">
                  <div className="common_radio_input type_blue2 lhFix">
                    <label>
                      <input
                        className="radio_input"
                        type="radio"
                        name="credit_mode"
                        checked={creditMode === 'each'}
                        onChange={() => setCreditMode('each')}
                      />
                      <span className="icon" />
                      <span className="label_text">
                        각 회사의 크레딧 사용
                        <span className="infoSetgroup4__subText">
                          외부 참석자들은 각 회사의 크레딧으로 번역을 사용합니다.
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </dd>
        </div>

        {/* ── 회의 설명 ───────────────────────────────────────── */}
        {translationEnabled && (
        <div>
          <dt className="textTop lh32">회의 설명</dt>
          <dd>
            <div className="common_textAreaNew" style={{ height: 100 }}>
              <textarea
                title="내용입력"
                placeholder="회의 주제를 입력해주세요."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </dd>
        </div>
        )}

        {/* ── 회의 키워드 ─────────────────────────────────────── */}
        {translationEnabled && (
        <div>
          <dt className="textTop lh32">회의 키워드</dt>
          <dd>
            <div className="rtcDialogSetupNew__infoSetgroup5">
              <div className="common_newTagBox notosans_font">
                {keywords.map((kw, idx) => (
                  <div key={idx} className="common_newTagBox__item">
                    <span className="common_newTagBox__text">{kw}</span>
                    <button
                      type="button"
                      className="common_newTagBox__delBtn"
                      onClick={() => handleKeywordRemove(idx)}
                    >
                      <span className="lunacon lunacon--multiply-outlined-thin" />
                      <span className="blind">삭제</span>
                    </button>
                  </div>
                ))}
                <div
                  className="common_newTagBox__inputItem"
                  style={{
                    flex: '1 1 140px',
                    minWidth: 140,
                    cursor: 'text',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                  onClick={() => keywordInputRef.current?.focus()}
                >
                  <input
                    ref={keywordInputRef}
                    type="text"
                    placeholder={keywords.length === 0 ? '키워드를 입력하고 Enter를 눌러주세요.' : '키워드 추가'}
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={handleKeywordKeyDown}
                    onBlur={handleKeywordAdd}
                    style={{
                      display: 'block',
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      width: '100%',
                      minWidth: 140,
                      height: 26,
                      padding: '0 4px',
                      fontSize: 13,
                      lineHeight: '26px',
                      color: '#333',
                      boxShadow: 'none',
                    }}
                  />
                </div>
              </div>
            </div>
          </dd>
        </div>
        )}
      </dl>
      )}

      {/* ── 하단 버튼 ──────────────────────────────────────────── */}
      <div className="dialog_btnbx" style={{ marginTop: 'auto' }}>
        <div>
          <button
            type="button"
            className={`common_round_btn rtcNew_btnType blue${isSubmitting ? ' disabled' : ''}`}
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? '처리중...' : '회의 설정 변경'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MeetingSettingsTab;
